export interface YoutubeMilestoneEvent {
  data: number;
  target: YT.Player;
}

export interface YoutubeApiManagerOptions {
  events?: {
    onReady?: (event: YT.PlayerEvent) => void;
    onStateChange?: (event: YT.OnStateChangeEvent) => void;
    onPlay?: (event: YT.OnStateChangeEvent) => void;
    onPause?: (event: YT.OnStateChangeEvent) => void;
    onComplete?: (event: YT.OnStateChangeEvent) => void;
    onError?: (event: YT.OnErrorEvent) => void;
    onPlaybackQualityChange?: (event: YT.OnPlaybackQualityChangeEvent) => void;
    onPlaybackRateChange?: (event: YT.OnPlaybackRateChangeEvent) => void;
    onMilestoneReached?: (event: YoutubeMilestoneEvent) => void;
  };
  milestones?: number[];
}

export const YOUTUBE_IFRAME_API = 'https://www.youtube.com/iframe_api';

export class YoutubeApiManager {
  private static readonly PROGRESS_INTERVAL = 250;

  private static readonly DEFAULT_MILESTONES = [25, 50, 75, 100];

  private static apiReadyPromise: Promise<void> | null = null;

  private player: YT.Player | null = null;

  private intervalRef: number | null = null;

  private options: YoutubeApiManagerOptions;

  private milestones: number[];

  private reachedMilestones: Record<number, boolean>;

  constructor(element: HTMLElement, options: YoutubeApiManagerOptions) {
    this.options = options;

    this.milestones = options.milestones || YoutubeApiManager.DEFAULT_MILESTONES;

    this.reachedMilestones = this.milestones.reduce((acc, milestone) => {
      acc[milestone] = false;
      return acc;
    }, {} as Record<number, boolean>);

    YoutubeApiManager.loadYoutubeIframeApi().then(() => {
      this.player = new window.YT.Player(element, {
        events: {
          onReady: (e) => this.onReady(e),
          onStateChange: (e) => this.onStateChange(e),
          onError: (e) => this.onError(e),
          onPlaybackQualityChange: (e) => this.onPlaybackQualityChange(e),
          onPlaybackRateChange: (e) => this.onPlaybackRateChange(e),
        },
      });
    });
  }

  static loadYoutubeIframeApi(): Promise<void> {
    if (typeof window.YT !== 'undefined' && typeof window.YT.Player !== 'undefined') {
      return Promise.resolve();
    }

    if (this.apiReadyPromise) {
      return this.apiReadyPromise;
    }

    this.apiReadyPromise = new Promise<void>((resolve) => {
      (window as any).onYouTubeIframeAPIReady = () => {
        resolve();
      };

      const existingScript = document.querySelector(`script[src="${YOUTUBE_IFRAME_API}"]`);
      if (!existingScript) {
        const tag = document.createElement('script');
        tag.src = YOUTUBE_IFRAME_API;
        tag.async = true;
        tag.defer = true;
        document.head.appendChild(tag);
      }
    });

    return this.apiReadyPromise;
  }

  destroy() {
    this.clearInterval();
    this.player?.destroy();
  }

  private onReady(event: YT.PlayerEvent) {
    this.options.events?.onReady?.(event);
  }

  private onStateChange(event: YT.OnStateChangeEvent) {
    this.options.events?.onStateChange?.(event);

    if (event.data === YT.PlayerState.PLAYING) {
      this.options.events?.onPlay?.(event);

      this.trackProgress();
    } else {
      this.clearInterval();
    }

    if (event.data === YT.PlayerState.PAUSED) {
      this.options.events?.onPause?.(event);
    }

    if (event.data === YT.PlayerState.ENDED) {
      this.options.events?.onComplete?.(event);
    }
  }

  private onError(event: YT.OnErrorEvent) {
    this.options.events?.onError?.(event);
  }

  private onPlaybackQualityChange(event: YT.OnPlaybackQualityChangeEvent) {
    this.options.events?.onPlaybackQualityChange?.(event);
  }

  private onPlaybackRateChange(event: YT.OnPlaybackRateChangeEvent) {
    this.options.events?.onPlaybackRateChange?.(event);
  }

  private trackProgress() {
    this.intervalRef = window.setInterval(() => {
      const percent = this.getCurrentPercent();

      this.milestones.forEach((milestone) => {
        if (percent >= milestone && !this.reachedMilestones[milestone]) {
          this.reachedMilestones[milestone] = true;

          if (this.player) {
            this.options.events?.onMilestoneReached?.({ data: percent, target: this.player });
          }
        }
      });
    }, YoutubeApiManager.PROGRESS_INTERVAL);
  }

  private getCurrentPercent() {
    const currentTime = this.player?.getCurrentTime() ?? 0;
    const duration = this.player?.getDuration() ?? 0;

    if (duration) {
      return (currentTime / duration) * 100;
    }

    return 0;
  }

  private clearInterval() {
    if (this.intervalRef) {
      clearInterval(this.intervalRef);
    }
  }
}
